import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, Phone, Mail, Anchor } from 'lucide-react';
import { Button } from './ui/button';
import { getCart } from '../mock/coastalMockData';

const CoastalHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const updateCartCount = () => {
      const cart = getCart();
      const count = cart.reduce((total, item) => total + item.quantity, 0);
      setCartCount(count);
    };
    
    updateCartCount();
    window.addEventListener('cartUpdated', updateCartCount);
    
    return () => window.removeEventListener('cartUpdated', updateCartCount);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'U.S. Flags', path: '/us-flags' },
    { name: 'Marine & Nautical', path: '/marine' },
    { name: 'Military', path: '/military' },
    { name: 'Poles & Hardware', path: '/poles' },
    { name: 'Custom Orders', path: '/custom' },
    { name: 'Installation', path: '/installation' }
  ];

  return (
    <header className="sticky top-0 z-50 bg-white shadow-md">
      {/* Top bar */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-cyan-700 text-white py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <a href="tel:205-587-2402" className="flex items-center gap-1 hover:text-cyan-300 transition-colors">
              <Phone className="w-4 h-4" />
              205-587-2402
            </a>
            <a href="mailto:coastalflagco@yahoo.com" className="flex items-center gap-1 hover:text-cyan-300 transition-colors">
              <Mail className="w-4 h-4" />
              coastalflagco@yahoo.com
            </a>
          </div>
          <div className="hidden md:block text-xs">
            Serving the Gulf Coast • Marine Grade Quality • Made in USA
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <Anchor className="w-8 h-8 text-blue-600" />
            <div className="text-2xl font-bold">
              <span className="text-blue-900">Coastal Flag</span>
              <span className="block text-lg font-semibold text-cyan-600">Company</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-slate-700 hover:text-blue-600 font-medium transition-colors"
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Cart and Mobile Menu */}
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              className="relative border-blue-600 text-blue-600 hover:bg-blue-50"
              onClick={() => navigate('/cart')}
            >
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-cyan-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                  {cartCount}
                </span>
              )}
            </Button>

            {/* Mobile menu button */}
            <Button
              variant="outline"
              size="icon"
              className="lg:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 border-t pt-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="block py-2 text-slate-700 hover:text-blue-600 font-medium transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};

export default CoastalHeader;